import React, { type ReactElement, useState } from "react";
import { FaUser } from "react-icons/fa";
import { Link, useI18next } from "gatsby-plugin-react-i18next";

import NavHomeIcon from "@components/icons/nav_home_icon";
import NavMyESIMsIcon from "@components/icons/nav_my_esims_icon";
import NavProfileIcon from "@components/icons/nav_profile_icon";
import NavStoreIcon from "@components/icons/nav_store_icon";
import NavWalletIcon from "@components/icons/nav_wallet_icon";
import Modal from "@components/modal";
import MobileMenuTabs from "@data/mobile-menu.yml";
import { type default as MobileMenuType } from "@interfaces/MobileMenu";
import { useAuthenticationState } from "@stores/authentication";
import { cn, defaultRipple, primaryRipple } from "@utilities/helper";

const MobileMenu = (): ReactElement => {
  const { t, navigate } = useI18next();
  const authenticationState = useAuthenticationState();
  const [openModal, setOpenModal] = useState<boolean>(false);

  const showNavMenu: boolean = false;

  const clickMenuItem = (menu: MobileMenuType): void => {
    if (menu.link === "/") {
      setOpenModal(false);
    } else {
      if (authenticationState.isAuthenticated()) {
        navigate(menu.link);
      } else {
        setOpenModal(true);
      }
    }
  };

  const getIcon = (menu: MobileMenuType): ReactElement => {
    let icon: ReactElement<any, any>;
    switch (menu.label) {
      case "home":
        icon = <NavHomeIcon isChecked={false} />;
        break;
      default:
      case "store":
        icon = <NavStoreIcon isChecked={menu.link === "/"} />;
        break;
      case "eSims":
        icon = <NavMyESIMsIcon isChecked={false} />;
        break;
      case "wallet":
        icon = <NavWalletIcon isChecked={false} />;
        break;
      case "profile":
        icon = <NavProfileIcon isChecked={false} />;
        break;
    }
    return icon;
  };

  return (
    <>
      {showNavMenu && authenticationState.isAuthenticated() && (
        <>
          <nav className="fixed inset-x-4 bottom-8 z-20 grid grid-flow-col justify-stretch gap-1 rounded-2xl border border-solid border-gray-500 bg-[#f5f5f5] p-2 shadow-xl shadow-gray-500 md:hidden">
            {MobileMenuTabs.map((menu: MobileMenuType, i: number) => {
              return (
                <div
                  key={"mobile_menu_" + i}
                  className="rounded-lg p-2"
                  ref={primaryRipple}
                >
                  <button
                    onClick={() => {
                      clickMenuItem(menu);
                    }}
                    className="group flex w-full flex-col items-center justify-center gap-2 text-[12px] no-underline outline-none"
                  >
                    <div className="flex h-6 items-center justify-center">
                      {getIcon(menu)}
                    </div>
                    <span
                      className={cn({
                        "text-primary": menu.link === "/",
                        "text-gray-400 transition duration-300 group-hover:text-primary/80":
                          menu.link !== "/",
                      })}
                    >
                      {t(menu.name)}
                    </span>
                  </button>
                </div>
              );
            })}
          </nav>
          <Modal
            isModalOpen={openModal}
            setIsModalOpen={setOpenModal}
            isCloseButtonExist={true}
            bgColor={"primary"}
          >
            <div className="flex flex-col">
              <div className="mx-auto mb-5 w-fit rounded-full bg-white/50 p-4">
                <FaUser className="size-[30px] text-white" />
              </div>

              <p className="whitespace-pre-wrap text-center font-semibold text-white">
                {t("sign_in_to_access")}
              </p>
              <Link
                ref={defaultRipple}
                to="/login"
                className="mx-auto mb-5 block w-fit rounded-3xl border border-solid border-white bg-white px-10 py-2 text-primary no-underline outline-none transition duration-300 hover:scale-105 focus:ring-2 focus:ring-white/80 focus:ring-offset-1"
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                {t("sign_in")}
              </Link>

              <p className="text-center text-white">
                {t("dont_have_an_account_already")}
                <Link
                  to="/register"
                  className="ml-2 text-white transition duration-300 hover:opacity-80 focus:outline-none"
                  placeholder={undefined}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                >
                  {t("register_here")}
                </Link>
              </p>
            </div>
          </Modal>
        </>
      )}
    </>
  );
};

export default MobileMenu;
